
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface ApiEndpoint {
  name: string;
  status: "up" | "down";
  lastChecked: Date;
  responseTime?: number; // Response time in milliseconds
}

const ApiStatusPanel = () => {
  const [endpoints, setEndpoints] = useState<ApiEndpoint[]>([
    { name: "url_screenshot", status: "up", lastChecked: new Date(), responseTime: 38 },
    { name: "url_product_information", status: "up", lastChecked: new Date(), responseTime: 42 },
    { name: "url_favicon", status: "up", lastChecked: new Date(), responseTime: 25 },
    { name: "url_social_link", status: "up", lastChecked: new Date(), responseTime: 31 },
  ]);
  
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [scintillateIndex, setScintillateIndex] = useState<number>(-1);
  const [metrics, setMetrics] = useState({
    totalRequests: 7237,
    averageResponseTime: 34,
    uptime: "99.999%",
  });

  // Update the current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);
  
  // Simulate periodic API status checks and metrics update
  useEffect(() => {
    const checkInterval = setInterval(() => {
      setEndpoints(prev => prev.map(endpoint => {
        // Generate random response time between 20-60ms
        const responseTime = Math.floor(Math.random() * 40) + 20;
        
        return {
          ...endpoint,
          status: "up",
          lastChecked: new Date(),
          responseTime
        };
      }));
      
      // Update total metrics
      setMetrics(prev => {
        const requestIncrement = Math.floor(Math.random() * 20) + 5;
        const newTotalRequests = prev.totalRequests + requestIncrement;
        const randomAverageChange = (Math.random() * 6) - 3; // -3 to +3 ms change
        const newAverage = Math.max(20, Math.min(60, prev.averageResponseTime + randomAverageChange));
        
        return {
          totalRequests: newTotalRequests,
          averageResponseTime: Math.floor(newAverage),
          uptime: "99.999%"
        };
      });
    }, 15000); // Check every 15 seconds
    
    return () => clearInterval(checkInterval);
  }, []);
  
  // Create scintillating effect
  useEffect(() => {
    const scintillateTimer = setInterval(() => {
      // Pick a random index or -1 (no scintillation)
      const randomIndex = Math.random() > 0.3 
        ? Math.floor(Math.random() * endpoints.length) 
        : -1;
      
      setScintillateIndex(randomIndex);
      
      // Reset after a short period
      setTimeout(() => {
        setScintillateIndex(-1);
      }, 300);
    }, 1000);
    
    return () => clearInterval(scintillateTimer);
  }, [endpoints.length]);

  // Format the time for display
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
  };
  
  // Format the date for display
  const formatDate = (date: Date) => {
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  return (
    <Card className="w-full border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
      <CardHeader className="pb-2 border-b border-slate-200 dark:border-slate-700">
        <CardTitle className="text-xl flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span>API Status</span>
            <Badge className="ml-2 text-xs bg-emerald-600">OPERATIONAL</Badge>
          </div>
          <div className="text-sm bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded-md">
            <span className="block">{formatDate(currentTime)} | {formatTime(currentTime)}</span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="flex flex-col space-y-4">
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-slate-100 dark:bg-slate-800 p-3 rounded-md">
              <div className="text-xs text-slate-500 dark:text-slate-400 mb-1 uppercase font-semibold">Total Requests</div>
              <div className="text-xl">{metrics.totalRequests.toLocaleString()}</div>
            </div>
            <div className="bg-slate-100 dark:bg-slate-800 p-3 rounded-md">
              <div className="text-xs text-slate-500 dark:text-slate-400 mb-1 uppercase font-semibold">Avg Response</div>
              <div className="text-xl">{metrics.averageResponseTime} ms</div>
            </div>
            <div className="bg-slate-100 dark:bg-slate-800 p-3 rounded-md">
              <div className="text-xs text-slate-500 dark:text-slate-400 mb-1 uppercase font-semibold">Uptime</div>
              <div className="text-xl text-emerald-600">{metrics.uptime}</div>
            </div>
          </div>
          
          <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 dark:bg-slate-800 text-left">
                <tr>
                  <th className="py-2 px-3 font-medium">Endpoint</th>
                  <th className="py-2 px-3 font-medium">Status</th>
                  <th className="py-2 px-3 font-medium text-right">Response Time</th>
                  <th className="py-2 px-3 font-medium text-right">Last Check</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {endpoints.map((endpoint, index) => (
                  <tr 
                    key={endpoint.name}
                    className={cn(
                      "bg-white dark:bg-slate-950",
                      scintillateIndex === index ? "bg-blue-50 dark:bg-blue-900/20" : ""
                    )}
                  >
                    <td className="py-2 px-3 flex items-center">
                      <div className="relative mr-2">
                        <span 
                          className={cn(
                            "block h-2 w-2 rounded-full",
                            "bg-emerald-500",
                            scintillateIndex === index ? "animate-pulse" : ""
                          )}
                        />
                        {scintillateIndex === index && (
                          <span className="absolute -inset-1 rounded-full animate-ping opacity-75 bg-blue-500"/>
                        )}
                      </div>
                      {endpoint.name}
                    </td>
                    <td className="py-2 px-3">
                      <Badge 
                        variant="success"
                        className="capitalize text-xs"
                      >
                        up
                      </Badge>
                    </td>
                    <td className="py-2 px-3 text-right">
                      {endpoint.responseTime} ms
                    </td>
                    <td className="py-2 px-3 text-right text-slate-500 dark:text-slate-400">
                      {formatTime(endpoint.lastChecked)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ApiStatusPanel;
